import { Divider } from 'antd5';
import i18n from 'i18next';
import { clone, get, isEmpty, omit, snakeCase } from 'lodash';
import { Link } from 'react-router-dom';

import { type TAnyAssetType } from 'components/data/assets/types';
import { None } from 'components/data/helpers';
import { StatusTag } from 'components/StatusTag';
import { type BreadcrumbItem } from 'components/ui';
import { type TColumnAttributes } from 'horizon/types/ColumnAttributes';
import {
  type AtlasGqlAsset,
  type AtlasGqlAssetFieldDefinition,
  type AtlasGqlAssetFieldValue,
  type AtlasGqlAssetFieldValueSelect,
  type AtlasGqlAssetFieldValueText,
  type AtlasGqlAssetType,
  type AtlasGqlTurbine,
  AtlasGqlInspectionSiteType,
} from 'types/atlas-graphql';
import { ASSET_UPDATE } from 'utils/access-control/rules';
import { getAssetPosition } from 'utils/asset-helpers';
import { ASSET_TYPE_SITE, ASSET_TYPE_TURBINE } from 'utils/constants';
import { selectObjectInput } from 'utils/editable';

export const createBreadcrumbTitle = (name: string, data: TAnyAssetType): string => {
  switch (name) {
    case 'Site':
      const siteUniqueIdentifier = getUniqueIdentifier(data);
      return siteUniqueIdentifier;
    case 'Turbine':
      const turbineUniqueIdentifier = getUniqueIdentifier(data);
      return turbineUniqueIdentifier;
    case 'Substation':
    case 'Transmission Structure':
      return getUniqueIdentifier(data);
    default:
      const assetPosition = getAssetPosition(data);
      return assetPosition ? `${name} ${assetPosition}` : name;
  }
};

export const createBreadcrumbItem = (data: any): BreadcrumbItem => {
  const { id, assetType } = data;
  const SUPPORTED_ASSET_ROUTES = ['Site', 'Turbine', 'Blade'];
  const typeName = assetType?.name ?? '';
  const urlSlug = SUPPORTED_ASSET_ROUTES.includes(typeName) ? `${snakeCase(typeName)}s` : 'assets';

  return {
    title: createBreadcrumbTitle(typeName, data as TAnyAssetType),
    href: `/${urlSlug}/${id}`,
  };
};

export const createSummaryData = (data: any[]): TColumnAttributes[] => {
  return data.map((item: any) => {
    return {
      key: item.fieldDefinition.id,
      title: item.fieldDefinition.name,
      dataIndex: item.fieldDefinition.id,
      // @ts-ignore 'React' refers to a UMD global, but the current file is a module. Consider adding an import instead.ts(2686)
      render: () => {
        if (item.fieldDefinition.name === 'Status') {
          return <StatusTag value={item.selectValue ?? ''} />;
        }

        if (item.textValue) {
          return item.textValue;
        }

        if (item.selectValue) {
          return item.selectValue;
        }

        return <None />;
      },
    };
  });
};

// Returns the value of the uniqueIdLabel field, with the "[AssetTypeName]: " prefix stripped out.
export const getStrippedUniqueIdLabel = (
  asset: AtlasGqlAsset,
  { keepTypeWithoutIdentifier = true }: { keepTypeWithoutIdentifier?: boolean } = {}
): string => {
  // If we don't have the uniqueIdLabel field, we can't strip it.
  if (!asset.uniqueIdLabel) {
    return '';
  }

  // If it includes "no serial number" or "no identifier", return it as is. Blades are weird like that.
  const lowerName = asset.uniqueIdLabel.toLowerCase();
  if (
    keepTypeWithoutIdentifier &&
    (lowerName.includes('no serial number') || lowerName.includes('no identifier'))
  ) {
    return asset.uniqueIdLabel;
  }

  const assetTypeName = asset.assetType?.name?.trim();
  if (assetTypeName) {
    const rest = asset.uniqueIdLabel.slice(assetTypeName.length);
    const hasAssetTypePrefix =
      asset.uniqueIdLabel.slice(0, assetTypeName.length).toLowerCase() ===
      assetTypeName.toLowerCase();

    if (!hasAssetTypePrefix) {
      return asset.uniqueIdLabel;
    }

    if (rest && !/^[\s:]/.test(rest)) {
      return asset.uniqueIdLabel;
    }

    return rest.trim().replace(/^:\s*/, '');
  }

  // Otherwise, strip out the single-token AssetTypeName prefix.
  const re = /^(\S)+: /;
  const finalName = asset.uniqueIdLabel.replace(re, '');
  return finalName;
};

export const getUniqueIdentifier = (data: AtlasGqlAsset | undefined): string => {
  if (!data) {
    return '';
  }

  if (data?.uniqueIdLabel) {
    return data.uniqueIdLabel;
  }

  const uniqueIdField: AtlasGqlAssetFieldValueText | null | undefined = data.fieldValues?.find(
    (item: AtlasGqlAssetFieldValue) => item.fieldDefinition?.isUniqueIdentifierField === true
  );

  if (uniqueIdField && uniqueIdField.textValue) {
    if (data.assetType?.name === 'Turbine') {
      const serialNumber = (data as AtlasGqlTurbine).serial;

      if (serialNumber) {
        return `${uniqueIdField.textValue.toString()}${i18n.t('asset.serialNumberSuffix', {
          defaultValue: ' (SN: {{serial}})',
          serial: serialNumber,
        })}`;
      }
    }

    return uniqueIdField.textValue.toString();
  }

  const positionValue = getAssetPosition(data);

  return [
    data.assetType?.name,
    positionValue,
    i18n.t('asset.noIdentifierField', {
      defaultValue: '(No {{field}})',
      field: uniqueIdField?.fieldDefinition.name ?? i18n.t('asset.identifier', 'identifier'),
    }),
  ].join(' ');
};

export const filterByAssetTypeIds = (values: string[]) => {
  return [{ key: 'assetTypeIds', values: values }];
};

export const generateParentAssetColumnData = (
  data: any,
  parentAssetOptionsData: any,
  canEditParent: boolean
) => {
  // The parent of a Turbine is a Site, whose `uniqueIdLabel` is prefixed with "Site: ". Remove the prefix by omitting `uniqueIdLabel`.
  const parent =
    data?.assetWithMetadata?.assetType?.name === 'Turbine'
      ? omit(data.assetWithMetadata.parent, 'uniqueIdLabel')
      : data?.assetWithMetadata?.parent;
  const parentUniqueIdentifier = getUniqueIdentifier(parent);

  const parentAssetOptions = parentAssetOptionsData.map((item: AtlasGqlAsset) => {
    return { id: item.id, name: getUniqueIdentifier(item) };
  });

  const parentColumn = {
    key: data.assetWithMetadata?.parent?.id,
    defaultValue: parentUniqueIdentifier,
    options: parentAssetOptions,
  };

  const parentAsset: TColumnAttributes = {
    key: data.assetWithMetadata?.parent?.id,
    title:
      data.assetWithMetadata?.parent?.assetType?.name ||
      i18n.t('table.parentAsset', 'Parent Asset'),
    dataIndex: data.assetWithMetadata?.parent?.id,
    get: (record: any) => get(record, 'parent'),
    render: () => (data.assetWithMetadata?.parent?.id ? parentUniqueIdentifier : <None />),
    edit: canEditParent ? selectObjectInput(parentColumn) : undefined,
    editRule: canEditParent ? ASSET_UPDATE : undefined,
  };

  return parentAsset;
};

type TrimmedAtlasGqlAssetType = Pick<AtlasGqlAssetType, 'name'>;

type TrimmedAtlasGqlAssetFieldDefinition = Pick<
  AtlasGqlAssetFieldDefinition,
  'isUniqueIdentifierField' | 'name'
>;

type TrimmedAtlasGqlAssetFieldValue = Pick<AtlasGqlAssetFieldValue, 'id'> & {
  fieldDefinition?: TrimmedAtlasGqlAssetFieldDefinition | null;
  textValue?: string | null;
  selectValue?: string | null;
};

type TrimmedAtlasGqlAsset = Pick<AtlasGqlAsset, 'id'> & {
  ancestors?: TrimmedAtlasGqlAsset[] | null;
  assetType?: TrimmedAtlasGqlAssetType | null;
  fieldValues?: (TrimmedAtlasGqlAssetFieldValue | null | undefined)[] | null;
};

export const getAssetSite = (
  asset?: TrimmedAtlasGqlAsset
): {
  identifier: string;
  site: TrimmedAtlasGqlAsset | undefined;
  siteType: AtlasGqlInspectionSiteType;
} => {
  const site =
    asset?.assetType?.name === 'Site'
      ? asset
      : asset?.ancestors?.find(a => a?.assetType?.name === 'Site');

  const siteTypeField = site?.fieldValues?.find(f => f?.fieldDefinition?.name === 'Type');
  const siteTypeFieldValue = (siteTypeField as AtlasGqlAssetFieldValueSelect)?.selectValue;
  const siteType =
    siteTypeFieldValue?.toUpperCase() === AtlasGqlInspectionSiteType.Solar
      ? AtlasGqlInspectionSiteType.Solar
      : AtlasGqlInspectionSiteType.Wind;

  const identifier = site ? getUniqueIdentifier(site) : '';

  return { identifier, site, siteType };
};

export const getAssetByType = (asset: AtlasGqlAsset | undefined | null, assetType: string) => {
  const assetByType =
    asset?.assetType?.name === assetType
      ? asset
      : asset?.ancestors?.find(a => a?.assetType?.name === assetType);

  return assetByType;
};

export const getOrderedAncestors = (asset: TrimmedAtlasGqlAsset): TrimmedAtlasGqlAsset[] => {
  return clone(asset.ancestors ?? []).reverse();
};

export const getAssetColumnCopy = (asset: TrimmedAtlasGqlAsset): string => {
  return getOrderedAncestors(asset)
    .filter(a => a.assetType?.name !== 'Site')
    .map(a => getUniqueIdentifier(a))
    .concat(getUniqueIdentifier(asset))
    .join(' ');
};

type RenderAssetOptions = {
  includeSite?: boolean;
};

export const renderAssetSite = (asset: TrimmedAtlasGqlAsset) => {
  const { identifier, site } = getAssetSite(asset);

  return site && !isEmpty(identifier) ? (
    <Link data-testid="asset-site-link" to={`/assets/${site.id}`}>
      {identifier}
    </Link>
  ) : (
    <None />
  );
};

export const renderAsset = (
  asset: TrimmedAtlasGqlAsset,
  { includeSite }: RenderAssetOptions = { includeSite: false }
): React.ReactNode => {
  if (asset.assetType?.name === 'Site') {
    return <None />;
  }

  return (
    <>
      {includeSite && (
        <>
          {renderAssetSite(asset)}
          <Divider type="vertical" />
        </>
      )}
      {getOrderedAncestors(asset)
        .filter(a => a.assetType?.name === 'Turbine')
        .map(a => (
          <span key={a.id}>
            {/* we want to override the default Asset Details tab when navigating from here */}
            <Link to={`/assets/${a.id}/inspections`}>{getUniqueIdentifier(a)}</Link>
            <Divider type="vertical" />
          </span>
        ))}
      {/* we want to override the default Asset Details tab when navigating from here */}
      <Link to={`/assets/${asset.id}/inspections`}>{getUniqueIdentifier(asset)}</Link>
    </>
  );
};

export const getTurbineIdByAsset = (asset: any) => {
  if (!asset) {
    return null;
  }

  const assetType = asset?.__typename;

  if (!assetType) {
    return null;
  }

  if (assetType !== ASSET_TYPE_SITE && assetType !== ASSET_TYPE_TURBINE) {
    const ancestorTurbine = asset.ancestors?.find((asset: any) => {
      return asset?.__typename === ASSET_TYPE_TURBINE;
    });
    return ancestorTurbine?.id;
  }

  if (assetType === ASSET_TYPE_TURBINE) {
    return asset.id;
  }

  return null;
};
